import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../../ui/button';
import { ChevronDownIcon, ArrowUpIcon, ArrowDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import PrecedentCard from './PrecedentCard';
import { Precedent } from '../../../../../types/case-workspace';
import ReportSectionHeading from './ReportSectionHeading';

type SortMode = 'similarity' | 'date';
type DateDirection = 'desc' | 'asc';

interface PrecedentsSectionProps {
  precedents: Precedent[];
  expandedPrecedents: string[];
  onTogglePrecedent: (drn: string) => void;
  onExpandAllPrecedents: () => void;
  onDecisionClick?: (decisionIdOrReference: string) => void;
  caseSummary?: string;
}

const PrecedentsSection: React.FC<PrecedentsSectionProps> = ({
  precedents,
  expandedPrecedents,
  onTogglePrecedent,
  onExpandAllPrecedents,
  onDecisionClick,
  caseSummary,
}) => {
  const [sortMode, setSortMode] = useState<SortMode>('similarity');
  const [dateDirection, setDateDirection] = useState<DateDirection>('desc');

  const sortedPrecedents = useMemo(() => {
    const sorted = [...precedents];
    if (sortMode === 'date') {
      const multiplier = dateDirection === 'desc' ? 1 : -1;
      sorted.sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return multiplier * (new Date(b.date).getTime() - new Date(a.date).getTime());
      });
    }
    // 'similarity' keeps the original order (already sorted by similarity from backend)
    return sorted;
  }, [precedents, sortMode, dateDirection]);

  const explorerUrl = caseSummary
    ? `/v3/ombudsman?tab=explorer&q=${encodeURIComponent(caseSummary)}`
    : null;

  return (
    <section className="border-b border-border px-6 py-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <ReportSectionHeading number={5} title={`Supporting precedent (${precedents.length})`} />
          </div>
          {explorerUrl && (
            <Link
              to={explorerUrl}
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline font-medium"
            >
              <MagnifyingGlassIcon className="w-3.5 h-3.5" />
              Explore in FOS corpus
            </Link>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md border border-border bg-background text-xs">
            <button
              onClick={() => setSortMode('similarity')}
              className={`px-2.5 py-1.5 rounded-l-md transition-colors ${
                sortMode === 'similarity'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              Relevance
            </button>
            <button
              onClick={() => {
                if (sortMode === 'date') {
                  setDateDirection(prev => prev === 'desc' ? 'asc' : 'desc');
                } else {
                  setSortMode('date');
                }
              }}
              className={`px-2.5 py-1.5 rounded-r-md transition-colors inline-flex items-center gap-1 ${
                sortMode === 'date'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              Date
              {sortMode === 'date' && (
                dateDirection === 'desc'
                  ? <ArrowDownIcon className="w-3 h-3" />
                  : <ArrowUpIcon className="w-3 h-3" />
              )}
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={onExpandAllPrecedents}>
            <ChevronDownIcon className="w-4 h-4 mr-1" />
            Expand All
          </Button>
        </div>
      </div>

      {/* Precedent Cards */}
      <div className="space-y-4">
        {sortedPrecedents.map((precedent) => (
          <PrecedentCard
            key={precedent.drn}
            precedent={precedent}
            isExpanded={expandedPrecedents.includes(precedent.drn)}
            onToggle={() => onTogglePrecedent(precedent.drn)}
            onDecisionClick={onDecisionClick}
          />
        ))}
      </div>
    </section>
  );
};

export default PrecedentsSection;
